import type { ApiHelper, ApiResponseWithData, BaseRequest } from '..';
import { ApiHelperWithData, splitParams } from '..';
import type {
  ApndFileSave,
  ApndUploadedFile,
  CnrcSave_Params1,
  CnrcSave_Params2,
  CnrcSave_Params3,
  ContractDetail,
  ContractList,
  ContractMemberStatusCnt,
  ContractMemberStatusList,
  PagerData,
} from '../model';

/*
 * 견적관리 API
 */
export class ContractApi {
  private withData: ApiHelperWithData;
  constructor(helper: ApiHelper) {
    this.withData = new ApiHelperWithData(helper);
  }

  // 목록 조회
  list = (
    params: {
      cnrcCntrFrom: string;
      cnrcCntrTo: string;
      bsClsfCd?: string;
      cnrcPrgsStat?: string;
      ceCoNm?: string;
      prjtNm?: string;
      rowsPerPage: number;
      pageNumber: number;
      sortColumn?: string;
      sortDir?: 'asc' | 'desc';
    } & BaseRequest,
  ): Promise<ApiResponseWithData<{ list: PagerData<ContractList> }>> => {
    const url = '/apis/contract/list';
    return this.withData.postJson(url, ...splitParams(params));
  };

  // 상세 조회
  detail = (
    params: {
      cnrcId: string;
      csesId: string;
    } & BaseRequest,
  ): Promise<ApiResponseWithData<{ detail: ContractDetail }>> => {
    const url = '/apis/contract/info';
    return this.withData.postJson(url, ...splitParams(params));
  };

  // 견적 ID로 연결된 계약 조회 (없으면 detail = null)
  infoByCses = (
    params: {
      csesId: string;
    } & BaseRequest,
  ): Promise<ApiResponseWithData<{ detail: ContractDetail | null }>> => {
    const url = '/apis/contract/infobycsesId';
    return this.withData.postJson(url, ...splitParams(params));
  };

  // 등록
  create = (
    params: {
      params1: CnrcSave_Params1;
      params2: CnrcSave_Params2[];
      params3: CnrcSave_Params3[];
      files: ApndFileSave[];
    } & BaseRequest,
  ): Promise<ApiResponseWithData<{ instCnt: number }>> => {
    const url = '/apis/contract/insert';
    return this.withData.postJson(url, ...splitParams(params));
  };

  // 수정
  update = (
    params: {
      params1: CnrcSave_Params1;
      params2: CnrcSave_Params2[];
      params3: CnrcSave_Params3[];
      files: ApndFileSave[];
    } & BaseRequest,
  ): Promise<ApiResponseWithData<{ instCnt: number }>> => {
    const url = '/apis/contract/update';
    return this.withData.postJson(url, ...splitParams(params));
  };

  // 삭제
  delete = (
    params: {
      cnrcId: string;
    } & BaseRequest,
  ): Promise<ApiResponseWithData<{ delCnt: number }>> => {
    const url = '/apis/contract/delete';
    return this.withData.postJson(url, ...splitParams(params));
  };

  // 파일 업로드
  uploadFile = (
    params: {
      file: Blob;
      fileName: string;
    } & BaseRequest,
  ): Promise<ApiResponseWithData<ApndUploadedFile>> => {
    const [data, extra] = splitParams(params);
    const formData = new FormData();
    formData.append('file', data.file, data.fileName);
    const url = '/apis/contract/upload';
    return this.withData.postMultipart(url, formData, extra);
  };

  // 파일 삭제 (임시 파일 정리)
  deleteFile = (
    params: {
      fileId: string;
    } & BaseRequest,
  ): Promise<ApiResponseWithData<void>> => {
    const url = '/apis/contract/delete-file';
    return this.withData.postJson(url, ...splitParams(params));
  };

  // 인력현황 목록
  cnrdMemStatusList = (
    params: {
      bsClsfCd?: string;
      prjtNm?: string;
      psClsfCd?: string;
      nm?: string;
      pageNumber: number;
      rowsPerPage: number;
      sortColumn?: string;
      sortDir?: 'asc' | 'desc';
    } & BaseRequest,
  ): Promise<ApiResponseWithData<{ list: PagerData<ContractMemberStatusList> }>> => {
    const url = '/apis/contract/member-status/list';
    return this.withData.postJson(url, ...splitParams(params));
  };

  // 투입현황 건수목록
  cnrdMemStatusCnt = (
    params: BaseRequest,
  ): Promise<ApiResponseWithData<{ list: ContractMemberStatusCnt[] }>> => {
    const url = '/apis/contract/member-status-cnt/list';
    return this.withData.post(url, ...splitParams(params));
  };
}
